import moment from 'moment';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import Badge from 'react-bootstrap/Badge';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import AutoSizer from 'react-virtualized-auto-sizer';
import { Spinner } from '../../../../../components/Spinner';
import VirtualizedList from '../../../../../components/VirtualizedList';
import { useAuth } from '../../../../../hooks/useAuth';
import { useEnv } from '../../../../../context/env-context';
import { makeRequest } from '../../../../../utils/makeRequest';
import { toast } from 'react-toastify';
import { getTextWidthInOneLine } from '../../../../../utils/strings';
import ConsumerNotificationModal from '../modals/ConsumerNotificationModal';
import IframeFullHeight from '../../../../../components/IframeFullHeight';
import TableMultiLineText from '../../../../../components/TableMultiLineText';

function ConsumerNotificationsTab({ consumerId, refreshTab, setRefreshTab }) {
  const { apiOriginNotifications } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [spinner, setSpinner] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);

  const tableColumns = useMemo(
    () => [
      {
        key: 'timestamp',
        header: 'Send Time',
        width: 160,
        renderValue: value =>
          value ? moment.utc(value).local().format('MMM DD, YYYY H:mm:ss') : '---',
      },
      {
        key: 'type',
        header: 'Type',
        width: 180,
        renderValue: (value, row) => (
          <OverlayTrigger
            trigger={getTextWidthInOneLine(value) > 150 ? ['hover', 'focus'] : []}
            placement="top"
            delay={{ show: 200 }}
            popperConfig={{ modifiers: [{ name: 'offset', options: { offset: [0, 0] } }] }}
            overlay={
              <Tooltip id={`type_${row.id}`} className="tooltip-max-width">
                {value}
              </Tooltip>
            }
          >
            <div className="text-truncate py-2">{value}</div>
          </OverlayTrigger>
        ),
      },
      {
        key: 'title',
        header: 'Title',
        width: 250,
      },
      {
        key: 'message',
        header: 'Message',
        width: 250,
        renderValue: (value, row) => (
          <TableMultiLineText value={value} uniqueId={`message_${row.id}`} lineClamp={3} />
        ),
      },
      {
        key: 'isRead',
        header: '',
        width: 80,
        renderValue: value =>
          value ? (
            <Badge className="ms-2 bo-inline-badge" bg="success">
              Read
            </Badge>
          ) : null,
      },
      {
        key: 'isDeleted',
        header: '',
        width: 100,
        renderValue: value =>
          value ? (
            <Badge className="ms-2 bo-inline-badge" bg="warning">
              Deleted
            </Badge>
          ) : null,
      },
    ],
    [],
  );

  const previewColumn = useMemo(
    () => ({
      key: 'bodyType',
      header: 'Body Preview',
      width: 400,
      renderValue: (value, row) =>
        value === 'TEXT' ? (
          <div className="bo-w-400 p-2">
            <div>{row.body}</div>
          </div>
        ) : (
          <IframeFullHeight title="Body Preview" htmlBody={row.htmlBody} width={400} />
        ),
    }),
    [],
  );

  const getRowHeight = useCallback(
    index => {
      const titleWidth = getTextWidthInOneLine(notifications[index].title);
      const titleHeight = titleWidth > 230 ? Math.ceil((titleWidth / 250) * 20) + 36 : 36;

      const messageWidth = getTextWidthInOneLine(notifications[index].message);
      const messageHeight = messageWidth > 230 ? Math.ceil((messageWidth / 250) * 20) + 36 : 36;

      return Math.max(titleHeight, messageHeight > 66 ? 66 : messageHeight, 36);
    },
    [notifications],
  );

  const getNotifications = useCallback(async () => {
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    setSpinner(true);

    const config = {
      token,
      url: `${apiOriginNotifications}/get_notifications`,
      method: 'GET',
      params: { consumerId },
    };

    try {
      const response = await makeRequest(config);

      setNotifications(response);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  }, [apiOriginNotifications, consumerId, getAccessTokenSilently]);

  useEffect(() => {
    getNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerId]);

  useEffect(() => {
    if (refreshTab === 'notifications') {
      getNotifications();
      setRefreshTab('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTab]);

  const sendNotification = async type => {
    setSpinner(true);
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginNotifications}/send_predefined_message`,
      method: 'POST',
      data: JSON.stringify({ consumerId, type }),
    };

    try {
      await makeRequest(config);

      getNotifications();

      toast.success(`${type} notification has been sent`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  const closeModal = () => setModalOpen(false);

  return (
    <>
      <div className="h-100 w-100">
        <div className="d-flex">
          <Dropdown className="m-3" disabled={spinner}>
            <Dropdown.Toggle className="bo-consumer-dropdown-toggle">
              Send Notification
            </Dropdown.Toggle>
            <Dropdown.Menu className="bo-consumer-dropdown-menu">
              <Dropdown.Item
                className="bo-consumer-dropdown-menu-item"
                onClick={() => setModalOpen(true)}
              >
                Free text
              </Dropdown.Item>
              {[
                ['SUSPENSION', 'Suspension'],
                ['TERMINATION', 'Termination'],
                ['PLAID_REFRESH_REQUIRED', 'Plaid Refresh Required'],
              ].map(([key, value]) => (
                <Dropdown.Item
                  key={key}
                  className="bo-consumer-dropdown-menu-item"
                  onClick={() => sendNotification(key)}
                >
                  {value}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <div id="popover_notification_form" />
        </div>
        <AutoSizer>
          {({ height, width }) => (
            <Spinner visible={spinner} overlayWidth={width}>
              <VirtualizedList
                tableRows={notifications}
                tableColumns={tableColumns}
                width={width}
                height={height - 58}
                rowKey="id"
                extendColumnKey="message"
                previewColumn={previewColumn}
                getRowHeight={getRowHeight}
                headerTopBorder
              />
            </Spinner>
          )}
        </AutoSizer>
      </div>
      <ConsumerNotificationModal
        modalOpen={modalOpen}
        closeModal={closeModal}
        consumerId={consumerId}
        getNotifications={getNotifications}
      />
    </>
  );
}

export default memo(ConsumerNotificationsTab);
